import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { exportApi, ExportRequest } from '../api/export';
import PremiumLayout from '../components/PremiumLayout';
import { 
  ArrowDownTrayIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const Export: React.FC = () => {
  const [formData, setFormData] = useState<ExportRequest>({
    entity_type: 'lead',
    format: 'csv',
    filters: {},
    fields: [],
  });
  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  const exportMutation = useMutation({
    mutationFn: async (request: ExportRequest) => {
      setExporting(true);
      setExportSuccess(false);
      try {
        const response = await exportApi.export(request);
        if (response.success && response.data.downloadUrl) {
          await exportApi.downloadFile(response.data.downloadUrl, response.data.fileName);
          setExportSuccess(true);
          setTimeout(() => setExportSuccess(false), 3000);
        } else {
          throw new Error('Export failed');
        }
      } catch (error: any) {
        console.error('Export error:', error);
        alert('Failed to export data. Please try again.');
      } finally {
        setExporting(false);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    exportMutation.mutate(formData);
  };

  const handleDownloadSample = async () => {
    try {
      await exportApi.downloadSample(formData.entity_type);
    } catch (error) {
      alert('Failed to download sample file. Please try again.');
      console.error('Error downloading sample:', error);
    }
  };

  return (
    <PremiumLayout
      title="Export Data"
      subtitle="Export your CRM data to CSV or Excel"
    >
      <div className="max-w-2xl mx-auto">
        <div className="premium-card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Entity Type */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Entity Type
                </label>
                <button
                  type="button"
                  onClick={handleDownloadSample}
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                  Download Sample
                </button>
              </div>
              <select
                value={formData.entity_type}
                onChange={(e) => setFormData({ ...formData, entity_type: e.target.value as any })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="lead">Leads</option>
                <option value="account">Accounts</option>
                <option value="contact">Contacts</option>
                <option value="activity">Activities</option>
                <option value="task">Tasks</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Download a sample Excel template to see the import format
              </p>
            </div>

            {/* Format */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Export Format
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="csv"
                    checked={formData.format === 'csv'}
                    onChange={(e) => setFormData({ ...formData, format: e.target.value as any })}
                    className="mr-2"
                  />
                  <DocumentArrowDownIcon className="h-5 w-5 mr-1" />
                  CSV
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="excel"
                    checked={formData.format === 'excel'}
                    onChange={(e) => setFormData({ ...formData, format: e.target.value as any })}
                    className="mr-2"
                  />
                  <DocumentArrowDownIcon className="h-5 w-5 mr-1" />
                  Excel
                </label>
              </div>
            </div>

            {/* Export Button */}
            <button
              type="submit"
              disabled={exporting}
              className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {exporting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  Exporting...
                </>
              ) : exportSuccess ? (
                <>
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Export Successful!
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                  Export Data
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </PremiumLayout>
  );
};

export default Export;
